import { storageImage, storageSrcSet } from "@/lib/imageUrl";
import { Camera, Film, Smartphone, ArrowRight, ChevronDown } from "lucide-react";
import { Link } from "@/lib/router-compat";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { useHeroImages } from "@/hooks/useHeroImages";
import { cn } from "@/lib/utils";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const MainServiceCards = () => {
  const sectionAnim = useScrollAnimation();
  const { data: heroImages } = useHeroImages();

  // Map service IDs to page identifiers
  const pageIdentifierMap: Record<string, string> = {
    'foto': 'photography',
    'film': 'videography',
    'social': 'socialmedia'
  };

  // Get hero image for a service
  const getServiceImage = (serviceId: string, defaultImage: string) => {
    const pageIdentifier = pageIdentifierMap[serviceId];
    const heroImage = heroImages?.find(h => h.page_identifier === pageIdentifier);
    return heroImage?.image_url || defaultImage;
  };

  const services = [
    {
      id: "foto",
      title: "Fotografie",
      subtitle: "STILL",
      description: "Bilder, die ohne Worte sprechen und Geschichten erzählen, die bleiben.",
      icon: Camera,
      path: "/foto",
      gradient: "from-primary/20 via-primary/10 to-transparent",
      image: "https://buhajnrjzecedelnxllv.supabase.co/storage/v1/object/public/project-media/covers/1760105191013-648ywxg9str.jpg",
      servicesList: [
        "Businessporträts & Corporate Headshots – Für professionelle Auftritte von Unternehmern, Teams und Führungskräften.",
        "Immobilien- & Architekturfotografie – Für Bauprojekte, Immobilienpräsentationen und Architekturportfolios.",
        "Produkt- & Foodfotografie – Für Marken, Restaurants und Onlineshops, die ihre Produkte hochwertig präsentieren möchten.",
        "Lifestyle- & Teamporträts – Für authentische Einblicke in Ihr Unternehmen und Ihre Unternehmenskultur.",
        "Kampagnenshootings – Für gezielte Markenbotschaften und kreative Werbekampagnen.",
        "Editorial- & Magazinfotografie – Für Veröffentlichungen in Print- und Online-Medien.",
        "Social-Media-Fotografie für Start-ups & Influencer – Für kreative Inhalte mit Wiedererkennungswert und Persönlichkeit.",
        "Drohnenfotografie – Für beeindruckende Luftaufnahmen und neue Perspektiven.",
        "Model- & Fashionfotografie – Für Modekampagnen, Lookbooks und kreative Modelporträts.",
        "Kampagnen- & Eventreportagen – Für lebendige Dokumentationen von Veranstaltungen und Markenauftritten.",
        "Website-Fotografie – Für hochwertige Bilder, die auf Ihrer Unternehmenswebsite Vertrauen schaffen und Professionalität ausstrahlen."
      ]
    },
    {
      id: "film",
      title: "Video",
      titleSecondLine: "produktion",
      subtitle: "MOTION",
      description: "Cinematische Videos, die Emotionen wecken und Ihre Marke lebendig machen.",
      icon: Film,
      path: "/film",
      gradient: "from-primary/20 via-primary/10 to-transparent",
      image: "https://buhajnrjzecedelnxllv.supabase.co/storage/v1/object/public/project-media/covers/1760106893964-jehm010q68.jpg",
      servicesList: [
        "Imagefilm / Unternehmensfilm – Erzählt die Geschichte und Expertise deines Unternehmens auf emotionale und authentische Weise.",
        "Industriefilm – Zeigt Produktionsprozesse, Maschinen und Innovationen direkt aus dem Herz des Unternehmens.",
        "Produktfilm – Präsentiert Produkte wirkungsvoll, erklärt ihre Vorteile und schafft Vertrauen bei Kund:innen.",
        "Werbefilm / Commercial – Hochwertige, cinematische Werbefilme für TV, Kino oder Online-Kampagnen.",
        "Promotionsfilm – Hebt gezielt Aktionen, Angebote oder Markteinführungen hervor und sorgt für maximale Aufmerksamkeit.",
        "Recruitingfilm / Employer Branding – Stärkt die Arbeitgebermarke und spricht gezielt neue Talente an.",
        "Schulungs- & E-Learning-Videos – Vermitteln Wissen, Prozesse oder Sicherheitsthemen verständlich und anschaulich.",
        "Erklärfilm (ohne Animation) – Vermittelt komplexe Themen durch klare Bilder, einfache Sprache und reale Szenen, damit Kund:innen Inhalte schnell verstehen.",
        "Dokumentation / Reportage – Zeigt echte Geschichten und Menschen hinter dem Unternehmen.",
        "Eventfilm / Messefilm – Fängt die Atmosphäre, Highlights und Emotionen von Veranstaltungen ein.",
        "Interview- & Testimonialfilm – Authentische Stimmen von Mitarbeiter:innen, Kund:innen oder Führungskräften vermitteln Glaubwürdigkeit und Nähe.",
        "Drohnenaufnahmen – Beeindruckende Luftaufnahmen für Außenansichten, Architektur und Unternehmensgelände."
      ]
    },
    {
      id: "social",
      title: "Social Media",
      subtitle: "CONTENT",
      description: "Strategischer Content, der Ihre Marke aufbaut und organisches Wachstum schafft.",
      icon: Smartphone,
      path: "/socialmedia",
      gradient: "from-primary/20 via-primary/10 to-transparent",
      image: "https://buhajnrjzecedelnxllv.supabase.co/storage/v1/object/public/project-media/gallery/1760101597818-hep3jl22i9i.jpg",
      servicesList: [
        "Social-Media-Strategie & Roadmap – Entwicklung einer klaren Strategie für organisches Wachstum, Markenaufbau und langfristige Reichweite.",
        "Content Creation (Posts, Carousels, Stories) – Kreative Inhalte, die Aufmerksamkeit wecken und die Markenidentität stärken.",
        "Redaktions- & Content-Planung – Strukturierte Planung und Veröffentlichung von Beiträgen für einen konsistenten Markenauftritt.",
        "Brand Storytelling & Messaging – Aufbau einer authentischen Markenstimme durch Geschichten, die im Gedächtnis bleiben.",
        "Profil- & Plattformoptimierung – Optimierung von Bios, Highlights und Layouts für maximale Sichtbarkeit und Wiedererkennung.",
        "Trend-Content & Newsjacking – Nutzung aktueller Trends und Themen für kreative, zeitnahe Inhalte.",
        "Content Repurposing & Adaption – Bestehende Inhalte neu aufbereiten und für verschiedene Plattformen anpassen.",
        "User Generated Content (UGC) – Integration und Wiederverwendung von Inhalten, die Kund:innen selbst erstellt haben.",
        "Influencer- & Micro-Influencer-Kooperationen (organisch) – Aufbau authentischer Partnerschaften für unbezahlte, natürliche Reichweite.",
        "Formatentwicklung & Innovation – Entwicklung neuer Content-Formate wie Behind-the-Scenes oder Interview-Reihen.",
        "Kamera-Coaching – Individuelles Coaching vor der Kamera, um authentisches Auftreten, Körpersprache und Ausdruck zu verbessern.",
        "Analytics & Reporting (organisch) – Analyse der organischen Performance zur Optimierung künftiger Inhalte.",
        "Tone of Voice & Style Guidelines – Definition eines einheitlichen Stils und einer klaren Markenstimme über alle Kanäle."
      ]
    }
  ];

  return (
    <section 
      ref={sectionAnim.ref}
      className={cn(
        "section-padding bg-gradient-to-b from-background via-background/95 to-background",
        "transition-all duration-1000",
        sectionAnim.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
      )}
    >
      <div className="container-custom">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            Unsere <span className="text-gradient glow-text">Leistungen</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Von professioneller Fotografie über cinematische Videos bis zu strategischem Social Media Content
          </p>
        </div>

        {/* Service Cards Grid */}
        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {services.map((service) => {
            const Icon = service.icon;
            return (
              <div
                key={service.id}
                className={cn(
                  "group relative overflow-hidden rounded-3xl cursor-pointer",
                  "border-2 border-primary/20 hover:border-primary/40",
                  "bg-gradient-to-br from-card/50 via-card to-card/80 backdrop-blur-xs",
                  "transition-all duration-500 hover:scale-[1.02] hover:shadow-2xl",
                  "hover:shadow-primary/20"
                )}
              >
                {/* Background Image with Overlay */}
                <div className="absolute inset-0 opacity-20 group-hover:opacity-30 transition-opacity duration-500">
                  <img
                    src={storageImage(getServiceImage(service.id, service.image), 800)}
                    alt={service.title}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    decoding="async"
                  />
                  <div className={cn(
                    "absolute inset-0 bg-gradient-to-br",
                    service.gradient
                  )} />
                </div>

                {/* Glow Effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-primary/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                
                {/* Content */}
                <div className="relative p-8 flex flex-col h-full min-h-[500px]">
                  {/* Icon */}
                  <div className="mb-6">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 border border-primary/20 group-hover:bg-primary/20 group-hover:border-primary/30 transition-all duration-300">
                      <Icon className="w-8 h-8 text-primary group-hover:scale-110 transition-transform duration-300" />
                    </div>
                  </div>

                  {/* Title */}
                  <h3 className="text-2xl md:text-3xl font-bold mb-4 group-hover:text-primary transition-colors duration-300">
                    {service.title}
                    {service.titleSecondLine && (
                      <>
                        <br className="xl:hidden" />
                        <span className="hidden xl:inline"> </span>
                        {service.titleSecondLine}
                      </>
                    )}
                  </h3>

                  {/* Description */}
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    {service.description}
                  </p>

                  {/* Leistungen Accordion */}
                  <Accordion type="single" collapsible className="relative z-10 w-full mb-auto">
                    <AccordionItem 
                      value="services" 
                      className="border-0"
                    >
                      <AccordionTrigger 
                        className="text-base font-semibold hover:no-underline p-0 mb-6 hover:gap-4 transition-all duration-300 [&[data-state=open]]:mb-4"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="flex items-center gap-2 text-primary">
                          <span>Leistungen</span>
                          <ChevronDown className="w-5 h-5 transition-transform duration-300" />
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="px-0 pb-6">
                        <div className="border border-primary/10 rounded-2xl bg-background/30 backdrop-blur-xs p-6">
                          <ul className="grid gap-2.5">
                            {service.servicesList.map((item, index) => {
                              const [name, ...descParts] = item.split('–');
                              const description = descParts.join('–');
                              return (
                                <li key={index} className="flex items-start gap-2.5 text-sm group/item">
                                  <div className="w-1 h-1 rounded-full bg-primary/50 mt-2 flex-shrink-0 group-hover/item:bg-primary transition-colors" />
                                  <span className="text-muted-foreground/90 leading-relaxed group-hover/item:text-muted-foreground transition-colors">
                                    <strong className="font-semibold text-foreground/80 group-hover/item:text-foreground transition-colors">{name}</strong>
                                    {description && ` – ${description}`}
                                  </span>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>

                  {/* CTA */}
                  <Link
                    to={service.path}
                    aria-label={`Mehr erfahren: ${service.title}${service.titleSecondLine ? ` ${service.titleSecondLine}` : ""}`}
                    className="flex items-center gap-2 text-primary font-semibold group-hover:gap-4 transition-all duration-300 after:absolute after:inset-0 after:content-['']"
                  >
                    <span>Mehr erfahren</span>
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                  </Link>
                </div>

                {/* Shine Effect on Hover */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
                  <div className="absolute top-0 -inset-full h-full w-1/2 z-5 block transform -skew-x-12 bg-gradient-to-r from-transparent to-white/10 group-hover:animate-[shimmer_1.5s] origin-top-left" />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default MainServiceCards;
